// eslint-disable-next-line no-restricted-imports
import type { FieldPath, FieldPathValue, FieldValues, UseFormReturn } from 'react-hook-form';

import type { ObjectDotNotation } from '@luna/utils-ts';

import type {
    CustomFieldError,
    CustomFieldErrors,
    FormValidationErrorMap,
} from './FormValidationError';
import type { ErrorDetails, TypedFieldName } from './type';
import { SERVER_ERROR_PATH } from './type';

export type GenericObject = { [key: string | number]: GenericObject | any };

export const get = (obj: unknown, path: string): GenericObject | undefined => {
    const objectForGet = obj as GenericObject;

    const pathArray = (path.match(/([^[.\]])+/g) || []) as string[];

    return pathArray.reduce(
        (prevObj, key) => prevObj && (prevObj[key] as GenericObject),
        objectForGet,
    );
};

export const set = (obj: unknown, path: string, value?: any): GenericObject => {
    const objectForMutation = obj as GenericObject;

    if (!value) {
        return objectForMutation;
    }

    const pathArray = (path.match(/([^[.\]])+/g) || []) as string[];

    // recursive reduce to set value to the object
    pathArray.reduce((acc, key, i) => {
        if (acc[key] === undefined) {
            acc[key] = {};
        }

        if (i === pathArray.length - 1) {
            acc[key] = value;
        }

        return acc[key] as GenericObject;
    }, objectForMutation);

    return objectForMutation;
};

export const isValidError = (error: CustomFieldErrors | CustomFieldError): boolean =>
    typeof error?.type === 'string' && typeof error?.message === 'string';

const mapErrorsToDotNotationRecursion = <FormValues>(
    errors: CustomFieldErrors | CustomFieldError,
    currentPath = '',
): FormValidationErrorMap<FormValues> => {
    if (isValidError(errors)) {
        // remove properties from FieldError type for next recursion
        const { type, message, ref, types, ...restErrors } = errors;

        // get nested errors
        const restDotNotationErrors = mapErrorsToDotNotationRecursion(restErrors, currentPath);

        // remove the first dot from the path
        const path = currentPath.replace(/^\./, '') as ObjectDotNotation<FormValues>;

        return {
            ...restDotNotationErrors,
            [path]: message,
        } as FormValidationErrorMap<FormValues>;
    }

    return Object.entries(errors).reduce((acc, [key, currentErrors]) => {
        if (!currentErrors || typeof currentErrors !== 'object') {
            return acc;
        }

        return {
            ...acc,
            ...mapErrorsToDotNotationRecursion(currentErrors, `${currentPath}.${key}`),
        };
    }, {} as FormValidationErrorMap<FormValues>);
};

export const mapErrorsToDotNotation = <FormValues>(
    errors: CustomFieldErrors,
): FormValidationErrorMap<FormValues> => mapErrorsToDotNotationRecursion<FormValues>(errors);

// Use this constant to compare your custom error type. Example: if (errorType === SUBMIT_ERROR_TYPE) {...}
export const SUBMIT_ERROR_TYPE = '_submitError';

export const setErrors = <FormValue extends Record<string, any>>(
    form: UseFormReturn<FormValue>,
    errors: Partial<Record<TypedFieldName<FormValue>, string>>,
): void => {
    const values = form.getValues();

    Object.keys(errors).forEach((errorKey) => {
        if (errorKey === SERVER_ERROR_PATH) {
            return;
        }

        form.setValue(
            errorKey as FieldPath<FormValue>,
            get(values, errorKey) as FieldPathValue<FieldValues, FieldPath<FormValue>>,
            {
                shouldTouch: true,
                shouldValidate: false,
            },
        );
    });

    // we need to set errors after the next tick, or it clashes with the setValue call
    // we use the 'root' error feature of react-hook-form, which clears it on each submission
    // https://react-hook-form.com/docs/useform/seterror
    setTimeout(() => {
        Object.entries(errors).forEach(([errorKey, errorMessage]) => {
            form.setError(
                `root.${errorKey}` as FieldPath<FormValue>,
                { type: SUBMIT_ERROR_TYPE, message: errorMessage as string },
                { shouldFocus: false },
            );
        });
    });
};

export const setError = <FormValue extends FieldValues>(
    form: Pick<UseFormReturn<FormValue>, 'setError'>,
    fieldName: FieldPath<FormValue>,
    { errorType, message, shouldFocus }: ErrorDetails,
): void => {
    // Without setTimeout, the error message won't be displayed immediately
    setTimeout(() => form.setError(fieldName, { type: errorType, message }, { shouldFocus }));
};
